/**
 * Student Management Page (Super Admin Only)
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Users,
  Search,
  RefreshCw,
  Lock,
  Unlock,
  FileText,
  ChevronLeft,
  ChevronRight,
  Eye,
  AlertTriangle,
  CheckCircle,
  X,
  Trash2,
  RotateCcw,
  User,
  Building,
  Shield,
  Zap,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/admin/AdminLayout';

const API_URL = window.location.origin;

const StudentManagement = () => {
  const navigate = useNavigate();
  const { token, logout } = useCMSAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [syncFilter, setSyncFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetails, setStudentDetails] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // Guard against stale fetches overwriting newer ones when filters change quickly
  const latestReqRef = useRef(0);

  const handleUnauthorized = (res) => {
    if (res.status === 401 || res.status === 403) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  useEffect(() => {
    loadStudents();
  }, [page, statusFilter, syncFilter]);

  const loadStudents = async () => {
    const reqId = ++latestReqRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        status: statusFilter,
        ...(search && { search }),
        ...(syncFilter && syncFilter !== 'all' && { sync_filter: syncFilter }),
      });

      const res = await fetch(`${API_URL}/api/admin/students?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Discard if a newer request has been issued in the meantime
      if (reqId !== latestReqRef.current) return;

      if (handleUnauthorized(res)) return;

      if (res.ok) {
        const data = await res.json();
        if (reqId !== latestReqRef.current) return;
        setStudents(data.students || []);
        setTotalPages(data.pages || 1);
        setTotal(data.total || 0);
      }
    } catch (error) {
      if (reqId === latestReqRef.current) toast.error('Failed to load students');
    } finally {
      if (reqId === latestReqRef.current) setLoading(false);
    }
  };

  const resetSearchCount = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/students/${userId}/reset-search`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (handleUnauthorized(res)) return;

      if (res.ok) {
        toast.success('Search count reset');
        loadStudents();
      } else {
        toast.error('Failed to reset search count');
      }
    } catch (error) {
      toast.error('Failed to reset search count');
    }
  };

  const toggleLock = async (userId, currentlyLocked) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/students/${userId}/${currentlyLocked ? 'unlock' : 'lock'}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (handleUnauthorized(res)) return;

      if (res.ok) {
        toast.success(currentlyLocked ? 'Account unlocked' : 'Account locked');
        loadStudents();
      } else {
        toast.error('Failed to update account status');
      }
    } catch (error) {
      toast.error('Failed to update account status');
    }
  };

  const viewStudentDetails = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/students/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (handleUnauthorized(res)) return;

      if (res.ok) {
        const data = await res.json();
        setStudentDetails(data);
        setSelectedStudent(userId);
        setShowDetailsModal(true);
        // Fetch Zoho sync status in parallel
        loadZohoSyncStatus(userId);
      } else {
        toast.error('Failed to load student details');
      }
    } catch (error) {
      toast.error('Failed to load student details');
    }
  };

  const [zohoSyncStatus, setZohoSyncStatus] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const [autoLinking, setAutoLinking] = useState(false);

  const handleAutoLinkZoho = async (userId) => {
    setAutoLinking(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/students/${userId}/auto-link-zoho`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success && data.found_in_zoho) {
        toast.success(`Linked to Zoho Lead ${data.zoho_lead_id}`);
        loadZohoSyncStatus(userId);
      } else {
        toast.error(data.message || data.detail || 'No matching lead found in Zoho');
      }
    } catch (error) {
      toast.error('Auto-link request failed');
    } finally {
      setAutoLinking(false);
    }
  };

  const loadZohoSyncStatus = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/students/${userId}/zoho-sync-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setZohoSyncStatus(data);
      } else {
        setZohoSyncStatus(null);
      }
    } catch {
      setZohoSyncStatus(null);
    }
  };

  const handleResyncDocsToZoho = async (userId) => {
    if (!window.confirm('Re-push all of this student\'s documents to Zoho CRM as a fresh Note?')) return;
    setResyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/students/${userId}/resync-docs-to-zoho`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Re-synced ${data.documents_synced} document(s) to Zoho`);
      } else {
        toast.error(data.error || data.message || 'Re-sync failed');
      }
      loadZohoSyncStatus(userId);
    } catch (error) {
      toast.error('Re-sync failed');
    } finally {
      setResyncing(false);
    }
  };

  const deleteDocument = async (userId, docType) => {
    if (!window.confirm('Delete this document?')) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/students/${userId}/documents/${docType}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (handleUnauthorized(res)) return;

      if (res.ok) {
        toast.success('Document deleted');
        viewStudentDetails(userId);
      } else {
        toast.error('Failed to delete document');
      }
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Student Management</h1>
          <p className="text-slate-500">Manage student accounts, search limits, and documents</p>
        </div>
        <Badge className="bg-rose-100 text-rose-700">Super Admin Only</Badge>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap items-center">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by ID, name, or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadStudents()}
            className="pl-9"
            data-testid="student-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }}
          className="px-4 py-2 border rounded-lg text-sm"
          data-testid="status-filter"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="locked">Locked</option>
        </select>
        <select
          value={syncFilter}
          onChange={(e) => { setPage(1); setSyncFilter(e.target.value); }}
          className="px-4 py-2 border rounded-lg text-sm"
          data-testid="sync-filter"
        >
          <option value="all">All Sync States</option>
          <option value="has_documents">Has documents</option>
          <option value="digilocker_verified">DigiLocker verified</option>
          <option value="zoho_synced">Zoho linked</option>
          <option value="pending_zoho">Zoho NOT linked</option>
        </select>
        <Button onClick={loadStudents} variant="outline" data-testid="refresh-students-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-white rounded-lg border">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-ux-blue" />
            <div>
              <p className="text-2xl font-bold">{total}</p>
              <p className="text-sm text-slate-500">Total Students</p>
            </div>
          </div>
        </div>
        <div className="p-4 bg-white rounded-lg border">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-emerald-500" />
            <div>
              <p className="text-2xl font-bold">{students.filter(s => !s.is_locked).length}</p>
              <p className="text-sm text-slate-500">Active</p>
            </div>
          </div>
        </div>
        <div className="p-4 bg-white rounded-lg border">
          <div className="flex items-center gap-3">
            <Lock className="w-8 h-8 text-rose-500" />
            <div>
              <p className="text-2xl font-bold">{students.filter(s => s.is_locked).length}</p>
              <p className="text-sm text-slate-500">Locked</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-x-auto">
        <table className="w-full min-w-[1100px]" data-testid="students-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Student</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Documents</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">DigiLocker</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Zoho Sync</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Profile</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Joined</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                  Loading...
                </td>
              </tr>
            ) : students.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                  No students found
                </td>
              </tr>
            ) : (
              students.map((student) => {
                const docCount = student.documents_count || 0;
                const digi = student.digilocker_status || {};
                const zoho = student.zoho_status || {};
                return (
                <tr
                  key={student.user_id}
                  className={student.is_locked ? 'bg-rose-50' : ''}
                  data-testid={`student-row-${student.user_id}`}
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-slate-900">{student.name || 'N/A'}</p>
                      <p className="text-sm text-slate-500">{student.email}</p>
                      <p className="text-[10px] text-slate-400 font-mono">{student.user_id}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3" data-testid={`docs-cell-${student.user_id}`}>
                    {docCount === 0 ? (
                      <span className="text-xs text-slate-400">—</span>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">
                          <FileText className="w-3 h-3 mr-1" />
                          {docCount}
                        </Badge>
                        {(student.documents_digilocker_count || 0) > 0 && (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px]">
                            <Shield className="w-3 h-3 mr-1" />
                            {student.documents_digilocker_count}
                          </Badge>
                        )}
                        {(student.documents_manual_count || 0) > 0 && (
                          <span className="text-[10px] text-slate-500">
                            {student.documents_manual_count} manual
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`digilocker-cell-${student.user_id}`}>
                    {digi.verified ? (
                      <Badge className="bg-green-100 text-green-800 border-green-200 text-[10px]">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-slate-300 text-slate-500 text-[10px]">
                        Pending
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3" data-testid={`zoho-cell-${student.user_id}`}>
                    {zoho.linked ? (
                      <div className="flex flex-col gap-0.5">
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px] w-fit">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Linked
                        </Badge>
                        {zoho.last_sync_at && (
                          <span className="text-[10px] text-slate-400">
                            {new Date(zoho.last_sync_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ) : (
                      <Badge variant="outline" className="border-amber-300 text-amber-700 text-[10px]">
                        Not linked
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-ux-blue rounded-full"
                          style={{ width: `${student.profile_completion || 0}%` }}
                        />
                      </div>
                      <span className="text-sm">{student.profile_completion || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {student.is_locked ? (
                      <Badge className="bg-rose-100 text-rose-700">
                        <Lock className="w-3 h-3 mr-1" />
                        Locked
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">
                    {student.created_at ? new Date(student.created_at).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => viewStudentDetails(student.user_id)}
                        data-testid={`view-student-${student.user_id}`}
                      >
                        <Eye className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => resetSearchCount(student.user_id)}
                        title="Reset search count"
                      >
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant={student.is_locked ? "default" : "destructive"}
                        onClick={() => toggleLock(student.user_id, student.is_locked)}
                      >
                        {student.is_locked ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-slate-500">
            Page {page} of {totalPages} ({total} total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && studentDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto m-4">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">Student Details</h2>
              <Button variant="ghost" size="sm" onClick={() => { setShowDetailsModal(false); setZohoSyncStatus(null); }}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6 space-y-6">
              {/* Profile */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Profile
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Name</p>
                    <p className="font-medium">{studentDetails.profile?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{studentDetails.profile?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Phone</p>
                    <p className="font-medium">{studentDetails.profile?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Search Count</p>
                    <p className="font-medium">{studentDetails.profile?.search_count || 0} / 5</p>
                  </div>
                </div>
              </div>

              {/* DigiLocker Verification Status */}
              {studentDetails.digilocker && (
                <div className="mb-4 p-4 rounded-lg border bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200" data-testid="digilocker-status-card">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Shield className={`w-5 h-5 ${studentDetails.digilocker.verified ? 'text-green-600' : 'text-slate-400'}`} />
                      <div>
                        <p className="font-semibold text-slate-900 text-sm">DigiLocker Verification</p>
                        <p className="text-xs text-slate-600">
                          {studentDetails.digilocker.verified
                            ? `Verified ${studentDetails.digilocker.documents_count || 0} document(s)`
                            : 'Not verified yet'}
                          {studentDetails.digilocker.aadhaar_linked ? ' • Aadhaar linked' : ''}
                        </p>
                      </div>
                    </div>
                    {studentDetails.digilocker.verified ? (
                      <Badge className="bg-green-100 text-green-800 border-green-200" data-testid="digilocker-verified-badge">
                        <CheckCircle className="w-3 h-3 mr-1" /> Verified
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-slate-300 text-slate-600">Pending</Badge>
                    )}
                  </div>
                  {studentDetails.digilocker.verified_at && (
                    <p className="text-xs text-slate-500 mt-2">
                      Verified on {new Date(studentDetails.digilocker.verified_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}

              {/* Zoho Doc Sync Status */}
              {zohoSyncStatus && (
                <div className="mb-4 p-4 rounded-lg border bg-slate-50">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold flex items-center gap-2 text-sm">
                        <Shield className="w-4 h-4 text-blue-600" />
                        Zoho Doc Sync
                        {zohoSyncStatus.is_linked ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-[10px]">
                            <CheckCircle className="w-3 h-3 mr-1" /> Lead linked
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Not linked
                          </Badge>
                        )}
                      </h3>
                      <div className="mt-2 text-xs text-slate-600 space-y-1">
                        {zohoSyncStatus.zoho_lead_id && (
                          <p>
                            <span className="text-slate-500">Lead ID:</span>{' '}
                            <a
                              href={`https://crm.zoho.com/crm/org20098319797/tab/Leads/${zohoSyncStatus.zoho_lead_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline font-mono"
                              data-testid="zoho-lead-link"
                            >
                              {zohoSyncStatus.zoho_lead_id}
                            </a>
                          </p>
                        )}
                        <p>
                          <span className="text-slate-500">Successful doc syncs:</span>{' '}
                          <span className="font-semibold">{zohoSyncStatus.doc_sync_success_count || 0}</span>
                        </p>
                        {zohoSyncStatus.last_doc_sync ? (
                          <p>
                            <span className="text-slate-500">Last doc sync:</span>{' '}
                            {new Date(zohoSyncStatus.last_doc_sync.timestamp).toLocaleString()}
                            {' • '}
                            <span className={zohoSyncStatus.last_doc_sync.status === 'success' ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>
                              {zohoSyncStatus.last_doc_sync.status}
                            </span>
                          </p>
                        ) : (
                          <p className="text-amber-700">No document sync attempts recorded yet</p>
                        )}
                        {zohoSyncStatus.last_doc_sync?.status === 'error' && zohoSyncStatus.last_doc_sync?.data?.error && (
                          <p className="text-red-600">
                            <span className="text-slate-500">Last error:</span> {zohoSyncStatus.last_doc_sync.data.error}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {!zohoSyncStatus.is_linked && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAutoLinkZoho(selectedStudent)}
                          disabled={autoLinking}
                          className="gap-2"
                          data-testid="auto-link-zoho-btn"
                          title="Search Zoho CRM by email and link the matching lead"
                        >
                          {autoLinking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                          Auto-link by Email
                        </Button>
                      )}
                      <Button
                        size="sm"
                        onClick={() => handleResyncDocsToZoho(selectedStudent)}
                        disabled={resyncing || !zohoSyncStatus.is_linked}
                        className="gap-2"
                        data-testid="resync-docs-to-zoho-btn"
                        title={zohoSyncStatus.is_linked ? 'Re-push all documents to Zoho as a new Note' : 'Link student to a Zoho Lead first'}
                      >
                        <RefreshCw className={`w-3 h-3 ${resyncing ? 'animate-spin' : ''}`} />
                        Re-sync to Zoho
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents */}
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Documents ({studentDetails.documents?.length || 0})
                </h3>
                {!studentDetails.documents || studentDetails.documents.length === 0 ? (
                  <p className="text-slate-500 text-sm">No documents uploaded</p>
                ) : (
                  <div className="space-y-2" data-testid="admin-student-documents">
                    {studentDetails.documents.map((doc, idx) => {
                      const isDigi = doc.source === 'digilocker' || doc.verified_via === 'digilocker';
                      const hasFile = !!(doc.cloudinary_url || doc.file_path);
                      const proxyUrl = `${API_URL}/api/admin/students/${selectedStudent}/documents/${doc.type}/view`;
                      const fileSizeKb = doc.file_size ? Math.round(doc.file_size / 1024) : null;
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-slate-50 rounded-lg gap-3"
                          data-testid={`admin-doc-row-${doc.type}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium truncate">{doc.type}</p>
                              {isDigi ? (
                                <Badge className="bg-blue-100 text-blue-800 border-blue-200 text-[10px]" data-testid={`doc-source-digilocker-${doc.type}`}>
                                  <Shield className="w-3 h-3 mr-1" /> DigiLocker
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600" data-testid={`doc-source-manual-${doc.type}`}>
                                  Manual upload
                                </Badge>
                              )}
                              {doc.verified && (
                                <Badge className="bg-green-100 text-green-800 border-green-200 text-[10px]">
                                  <CheckCircle className="w-3 h-3 mr-1" /> Verified
                                </Badge>
                              )}
                              {fileSizeKb !== null && (
                                <span
                                  className={`text-[10px] px-1.5 py-0.5 rounded ${fileSizeKb === 0 ? 'bg-red-100 text-red-700' : 'bg-slate-200 text-slate-600'}`}
                                  title={fileSizeKb === 0 ? 'Empty file - upload may have failed' : 'File size'}
                                >
                                  {fileSizeKb === 0 ? '⚠ 0 KB' : `${fileSizeKb} KB`}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1 truncate">
                              {doc.filename || '—'}
                              {doc.issuer ? ` • ${doc.issuer}` : ''}
                              {doc.uploaded_at ? ` • ${new Date(doc.uploaded_at).toLocaleDateString()}` : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {hasFile && (
                              <a
                                href={`${proxyUrl}?token=${encodeURIComponent(token)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline"
                                data-testid={`admin-doc-view-${doc.type}`}
                                onClick={(e) => {
                                  // Fetch through proxy with Authorization header and open as blob
                                  e.preventDefault();
                                  fetch(proxyUrl, { headers: { 'Authorization': `Bearer ${token}` } })
                                    .then(r => {
                                      if (!r.ok) throw new Error(`HTTP ${r.status}`);
                                      return r.blob();
                                    })
                                    .then(blob => {
                                      const url = URL.createObjectURL(blob);
                                      window.open(url, '_blank', 'noopener,noreferrer');
                                      setTimeout(() => URL.revokeObjectURL(url), 60000);
                                    })
                                    .catch(err => toast.error(`Could not open document: ${err.message}`));
                                }}
                              >
                                View
                              </a>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteDocument(selectedStudent, doc.type)}
                              data-testid={`admin-doc-delete-${doc.type}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default StudentManagement;
